<script setup lang="ts">
import { ChevronDown as ChevronDownIcon, UserCircle as UserCircleIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { ref, computed, nextTick, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'

import ProfileMenuContent from '@/components/auth/ProfileMenuContent.vue'
import ProfileSheet from '@/components/map/ProfileSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import { isTauri } from '@/services/platformService'
import { useAuthStore } from '@/stores/authStore'
import { useOfflineStore } from '@/stores/offlineStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

interface Emits {
  'open-auth-modal': []
  'open-profile-edit': []
  'open-sync-dialog': []
  'open-static-site': [pageId: string]
}

const emit = defineEmits<Emits>()
const props = withDefaults(
  defineProps<{
    variant?: 'default' | 'mobile-tab'
  }>(),
  {
    variant: 'default',
  },
)

// Auth store
const authStore = useAuthStore()
const offlineStore = useOfflineStore()
const recordingStore = useRecordingStore()
const uiStore = useUiStore()
const router = useRouter()
const { isAuthenticated, user, isLoading } = storeToRefs(authStore)

// Menu state
const isMenuOpen = ref(false)
const isSmallScreenMenu = computed(() => window.innerWidth < 640)
const isTouchDevice = computed(() => window.matchMedia('(pointer: coarse)').matches)
const menuRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownPosition = ref<{ top: number; left: number }>({ top: 0, left: 0 })

// User initials for avatar
const userInitials = computed(() => {
  if (!user.value?.name) {
    // Fallback to email first letter if no name
    return user.value?.email?.charAt(0).toUpperCase() || '?'
  }

  // Split name by spaces and take first letter of each word (up to 2 words)
  const nameParts = user.value.name.trim().split(/\s+/)
  if (nameParts.length >= 2) {
    return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
  }
  return nameParts[0].charAt(0).toUpperCase()
})

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

// Handle logout
const handleLogout = async () => {
  closeMenu()
  await authStore.logout()
}

// Handle login button click
const handleLogin = () => {
  emit('open-auth-modal')
}

// Handle static site links
const handleStaticSite = (pageId: string) => {
  closeMenu()
  emit('open-static-site', pageId)
}

// Handle profile edit button click
const handleProfileEdit = async () => {
  closeMenu()
  await nextTick()
  emit('open-profile-edit')
}

const handleNavigateToHome = async () => {
  closeMenu()
  await router.push({ name: 'landing-page' })
}

const handleNavigateToInvitations = async () => {
  closeMenu()
  await router.push({ name: 'account-invitations' })
}

const handleNavigateToOffline = async () => {
  closeMenu()
  await router.push({ name: 'offline-data' })
}

const handleOpenSyncDialog = () => {
  closeMenu()
  emit('open-sync-dialog')
}

const handleOpenRecording = () => {
  closeMenu()
  recordingStore.openModal()
}

// Handle click outside menu. The mobile sheet is teleported (not under dropdownRef), so also
// treat taps inside it as "inside" — taps on the map / another tab still dismiss.
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  const insideButton = menuRef.value?.contains(target)
  const insideDropdown = dropdownRef.value?.contains(target)
  const insideSheet = (target as HTMLElement | null)?.closest?.('[data-testid="profile-sheet"]')
  if (!insideButton && !insideDropdown && !insideSheet) {
    closeMenu()
  }
}

// Track the mobile account sheet centrally so the create FAB hides behind it (like the other
// mobile tab surfaces). Desktop dropdown does not occupy the map, so it never sets the flag.
watch(
  [isMenuOpen, isSmallScreenMenu],
  ([open, small]) => {
    uiStore.setProfileSheetOpen(open && small)
    // Profile and Search are mutually exclusive mobile surfaces — opening the account sheet
    // dismisses search so they never stack.
    if (open && small) uiStore.closeSmartSearch()
  },
  { immediate: true },
)
onBeforeUnmount(() => uiStore.setProfileSheetOpen(false))

// Handle escape key
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMenuOpen.value) {
    closeMenu()
  }
}

useEventListener(document, 'click', handleClickOutside)
useEventListener(document, 'keydown', handleEscape)

// Reposition dropdown when opened and on viewport changes
const updateDropdownPosition = () => {
  if (!buttonRef.value) return
  const rect = buttonRef.value.getBoundingClientRect()
  // Width of menu is w-64 (256px). Keep 16px margin to viewport edge.
  const MENU_WIDTH = 256
  const MARGIN = 16
  const left = Math.max(MARGIN, rect.right - MENU_WIDTH)
  const top = rect.bottom + 8 // Slightly larger than mt-2 for spacing
  dropdownPosition.value = { top, left }
}

watch(
  () => isMenuOpen.value,
  async (open) => {
    if (open) {
      await nextTick()
      updateDropdownPosition()
    }
  },
)

useEventListener(window, 'resize', () => {
  if (isMenuOpen.value) updateDropdownPosition()
})
useEventListener(window, 'scroll', () => {
  if (isMenuOpen.value) updateDropdownPosition()
})
</script>

<template>
  <div
    ref="menuRef"
    :class="
      props.variant === 'mobile-tab' ? 'relative flex h-full w-full justify-center' : 'relative'
    "
  >
    <!-- Authenticated user menu -->
    <div
      v-if="isAuthenticated && user"
      :class="
        props.variant === 'mobile-tab'
          ? 'flex h-full w-full items-stretch justify-center'
          : 'flex items-center'
      "
    >
      <!-- User avatar/menu button -->
      <button
        ref="buttonRef"
        @click="toggleMenu"
        :class="
          props.variant === 'mobile-tab'
            ? 'flex h-full w-full items-center justify-center hover:cursor-pointer'
            : 'flex items-center gap-1 hover:cursor-pointer'
        "
        :aria-expanded="isMenuOpen"
        aria-haspopup="true"
        :title="user.name || user.email"
      >
        <!-- Mobile tab: avatar + label inside the active lozenge (matches MobileTabBar) -->
        <span
          v-if="props.variant === 'mobile-tab'"
          class="flex w-full flex-col items-center justify-center gap-0.5 rounded-2xl py-1.5 text-[11px] font-medium transition-colors"
          :class="isMenuOpen ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'text-gray-500'"
        >
          <span
            class="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-semibold text-white"
          >
            {{ userInitials }}
          </span>
          <span class="max-w-full truncate">Profile</span>
        </span>

        <!-- Desktop: avatar + chevron inline -->
        <template v-else>
          <span
            class="flex items-center justify-center w-8 h-8 bg-emerald-600 text-white text-sm font-semibold rounded-full hover:bg-emerald-700 transition-colors duration-150"
          >
            {{ userInitials }}
          </span>
          <ChevronDownIcon
            class="w-4 h-4 text-gray-500 transition-transform duration-150"
            :class="{ 'rotate-180': isMenuOpen }"
          />
        </template>
      </button>

      <!-- Desktop dropdown (≥ sm): compact menu anchored under the avatar -->
      <Teleport to="body">
        <div
          v-if="isMenuOpen && !isSmallScreenMenu"
          ref="dropdownRef"
          class="fixed w-64 max-w-[calc(100vw-2rem)] bg-white py-1 rounded-md shadow-lg border border-gray-200"
          :style="{
            zIndex: Z_INDEX.MODAL,
            top: dropdownPosition.top + 'px',
            left: dropdownPosition.left + 'px',
          }"
          role="menu"
          aria-orientation="vertical"
        >
          <ProfileMenuContent
            dense
            :user-name="user.name || user.email"
            :user-email="user.name ? user.email : ''"
            :initials="userInitials"
            :recording-supported="recordingStore.isSupported"
            :is-tauri-app="isTauri()"
            :offline-size="offlineStore.totalSize"
            :is-touch-device="isTouchDevice"
            :is-loading="isLoading"
            @edit-profile="handleProfileEdit"
            @home="handleNavigateToHome"
            @record="handleOpenRecording"
            @invitations="handleNavigateToInvitations"
            @offline="handleNavigateToOffline"
            @sync="handleOpenSyncDialog"
            @static-site="handleStaticSite"
            @logout="handleLogout"
          />
        </div>
      </Teleport>

      <!-- Mobile (< sm): floating account sheet matching the Canvas / Collection drawers -->
      <Teleport to="body">
        <ProfileSheet v-if="isMenuOpen && isSmallScreenMenu">
          <ProfileMenuContent
            :user-name="user.name || user.email"
            :user-email="user.name ? user.email : ''"
            :initials="userInitials"
            :recording-supported="recordingStore.isSupported"
            :is-tauri-app="isTauri()"
            :offline-size="offlineStore.totalSize"
            :is-touch-device="isTouchDevice"
            :is-loading="isLoading"
            @edit-profile="handleProfileEdit"
            @home="handleNavigateToHome"
            @record="handleOpenRecording"
            @invitations="handleNavigateToInvitations"
            @offline="handleNavigateToOffline"
            @sync="handleOpenSyncDialog"
            @static-site="handleStaticSite"
            @logout="handleLogout"
          />
        </ProfileSheet>
      </Teleport>
    </div>

    <!-- Unauthenticated state - Log in text button -->
    <div
      v-else
      :class="
        props.variant === 'mobile-tab'
          ? 'flex h-full w-full items-stretch justify-center'
          : 'flex items-center'
      "
    >
      <button
        @click="handleLogin"
        :class="
          props.variant === 'mobile-tab'
            ? 'flex h-full w-full min-w-0 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-gray-500 hover:text-emerald-700 transition-colors cursor-pointer'
            : 'inline-flex items-center px-4 py-1.5 text-sm font-medium text-stone-700 border border-stone-200 rounded-lg hover:border-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 transition-colors cursor-pointer'
        "
        :disabled="isLoading"
        aria-label="Log in"
      >
        <template v-if="props.variant === 'mobile-tab'">
          <UserCircleIcon class="h-5 w-5" />
          <span class="max-w-full truncate">Profile</span>
        </template>
        <template v-else>Log in</template>
      </button>
    </div>
  </div>
</template>
